/**
 * Sorting for report tables.
 *
 * **Blank values sort last in both directions.** A row with no measurable response
 * time has no place at the top of a "fastest" sort or a "slowest" one — the value is
 * absent, not extreme. Eight report tables used to coerce null to '' (or to 0 on
 * numeric columns), which put every "—" row first as the fastest response. The
 * Caregiver table had already been fixed this way; this is that rule, shared.
 *
 * Numeric columns: pass their keys in `numericKeys`. Values are parsed with
 * parseFloat (the API returns many numbers as strings), and anything that does not
 * parse is treated as blank. Two values that are both real numbers compare
 * numerically even without being listed.
 *
 * Everything else: natural string order (`localeCompare`, `numeric: true`), so
 * "Room 2" sorts before "Room 10".
 */

const isBlank = (v) => v === null || v === undefined || v === '';

/**
 * @param {*} av
 * @param {*} bv
 * @param {'asc'|'desc'} [order='asc']
 * @param {boolean} [numeric=false] — parse both sides as numbers
 * @returns {number}
 */
export function compareValues(av, bv, order = 'asc', numeric = false) {
  const dir = order === 'desc' ? -1 : 1;

  if (numeric) {
    const an = isBlank(av) ? NaN : parseFloat(av);
    const bn = isBlank(bv) ? NaN : parseFloat(bv);
    const aMissing = !Number.isFinite(an);
    const bMissing = !Number.isFinite(bn);
    if (aMissing && bMissing) return 0;
    if (aMissing) return 1;   // last, regardless of direction
    if (bMissing) return -1;
    return dir * (an - bn);
  }

  const aBlank = isBlank(av);
  const bBlank = isBlank(bv);
  if (aBlank && bBlank) return 0;
  if (aBlank) return 1;
  if (bBlank) return -1;
  if (typeof av === 'number' && typeof bv === 'number') return dir * (av - bv);
  return dir * String(av).localeCompare(String(bv), undefined, { numeric: true });
}

/**
 * Sorted copy of `rows` by `key`. Returns `rows` itself when there is no key, so a
 * caller's "unsorted" state keeps the original order and identity.
 *
 * @param {Array<object>} rows
 * @param {string|null|undefined} key
 * @param {'asc'|'desc'} order
 * @param {{ numericKeys?: string[] }} [opts]
 */
export function sortRows(rows, key, order, { numericKeys = [] } = {}) {
  if (!key || !Array.isArray(rows)) return rows;
  const numeric = numericKeys.includes(key);
  return [...rows].sort((a, b) => compareValues(a?.[key], b?.[key], order, numeric));
}
